// Single shared-password gate. Verifies via /api/check-auth, stores the
// password in sessionStorage on success, and re-renders the app via the
// onUnlock callback. Cheapest defensible auth for a controlled-access MVP.
import { useState } from 'react';
import { setAuthToken, verifyPassword } from '../lib/api.js';

export default function PasswordGate({ onUnlock, onBack }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e?.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await verifyPassword(password);
      if (result.ok) {
        setAuthToken(password);
        onUnlock();
      } else {
        setError(result.error || 'Wrong password');
        setPassword('');
      }
    } catch (err) {
      setError(err?.message || 'Could not reach the server');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-5 rounded-lg border border-navy-700 bg-navy-800 p-8 shadow-xl"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gold text-lg font-bold text-navy-900">
            R
          </div>
          <div>
            <div className="text-base font-semibold tracking-wide text-white">RegDefence AI</div>
            <div className="text-xs text-slate-400">Restricted access preview</div>
          </div>
        </div>

        <div>
          <label htmlFor="rd-password" className="mb-1 block text-xs uppercase tracking-wide text-slate-400">
            Access password
          </label>
          <input
            id="rd-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            autoFocus
            autoComplete="current-password"
            className="w-full rounded-md border border-navy-700 bg-navy-900 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none disabled:opacity-50"
            placeholder="Enter the shared password"
          />
        </div>

        {error && (
          <div className="rounded-md border border-red-900/50 bg-red-900/20 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !password}
          className="w-full rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Checking…' : 'Unlock'}
        </button>

        <p className="border-t border-navy-700 pt-4 text-xs leading-relaxed text-slate-400">
          This is a controlled-access preview. If you don't have a password,
          contact Cognitive Compliance Limited.
        </p>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="block w-full text-center text-xs text-slate-400 underline hover:text-white"
          >
            ← Back to overview
          </button>
        )}
      </form>
    </div>
  );
}
